/**
 * @file history-coordinator.ts
 * @description Handles grouping actions and history manager extensions for the UI.
 */

import { HistoryManager, historyManager as coreHM, g } from '@hcie/core';
import { EventBus } from '@hcie/shared';

class GroupAction {
    constructor(public description: string, public actions: any[]) {}
    undo() { 
        for (let i = this.actions.length - 1; i >= 0; i--) {
            this.actions[i].undo();
        }
    }
    redo() { 
        for (let i = 0; i < this.actions.length; i++) {
            this.actions[i].redo();
        }
    }
}

export function extendHistoryManager(hm: any, doc: any = null) {
    if (!hm) return;
    
    // Prototype patching handled in patchWindowHistoryManager for the whole class.
    // We only need to link the specific document reference if provided.
    if (doc) hm._docRef = doc;
    
    // Force a UI refresh for the instance
    emitHistoryChanged(hm);
}

const docHistories = new Map<string, HistoryManager>();

/**
 * Gets or creates a HistoryManager for a specific document.
 */
export function getHistoryForDoc(docId: string): HistoryManager {
    const doc = g.documents.find(d => d.id === docId) as any;
    if (doc) {
        if (!doc.history) {
            const HMClass = (window as any).HistoryManager || HistoryManager;
            doc.history = new HMClass(g.max_undo_steps || 200);
            extendHistoryManager(doc.history, doc);
        } else {
            // Ensure it's patched even if it exists
            extendHistoryManager(doc.history, doc);
        }
        return doc.history;
    }
    
    // Fallback search in internal map for background/legacy docs
    if (!docHistories.has(docId)) {
        const hm = new HistoryManager(g.max_undo_steps || 200);
        const doc = g.documents.find(d => d.id === docId);
        extendHistoryManager(hm, doc);
        docHistories.set(docId, hm);
    }
    return docHistories.get(docId)!;
}

export function patchWindowHistoryManager(): void {
    const HMClass = (window as any).HistoryManager || HistoryManager;
    if (!HMClass || !HMClass.prototype) {
        setTimeout(patchWindowHistoryManager, 200);
        return;
    }

    if ((HMClass as any)._uiPatched) return;
    (HMClass as any)._uiPatched = true;

    const proto = HMClass.prototype;

    // 1. Patch PUSH
    const originalPush = proto.push;
    proto.push = function(action: any) {
        if (this._currentGroup) {
            this._currentGroup.actions.push(action);
        } else {
            originalPush.call(this, action);
            if (this._docRef) this._docRef.modified = true;
            emitHistoryChanged(this);
        }
    };

    // 2. Patch UNDO/REDO
    const originalUndo = proto.undo;
    proto.undo = function() {
        originalUndo.call(this);
        emitHistoryChanged(this);
    };

    const originalRedo = proto.redo;
    proto.redo = function() {
        originalRedo.call(this);
        emitHistoryChanged(this);
    };

    // 3. Add Grouping methods to prototype
    proto.beginGroup = function(description = 'Çoklu İşlem') {
        if (!this._groupStack) this._groupStack = [];
        const group = { description, actions: [] };
        this._groupStack.push(group);
        this._currentGroup = group;
    };

    proto.endGroup = function() {
        if (!this._groupStack || this._groupStack.length === 0) return;
        const group = this._groupStack.pop();
        this._currentGroup = this._groupStack.length > 0 ? this._groupStack[this._groupStack.length - 1] : null;
        
        if (group && group.actions.length > 0) {
            if (group.actions.length === 1) {
                this.push(group.actions[0]);
            } else {
                originalPush.call(this, new GroupAction(group.description, group.actions));
                emitHistoryChanged(this);
            }
        }
    };
}

/**
 * Renders the history list in the #history-pane .history-list element.
 * Fallback for when window.updateHistoryUI is missing.
 */
function updateHistoryList(actions: any[], currentIndex: number) {
    const pane = document.getElementById('history-pane');
    if (!pane) return;
    const list = pane.querySelector('.history-list');
    if (!list) return;

    if (actions.length > 0) {
        console.log(`[History-UI] Rendering ${actions.length + 1} items (including Open). Current Index: ${currentIndex}`);
    }

    list.innerHTML = '';

    // Always show initial "Open" state
    const openItem = document.createElement('div');
    openItem.className = 'history-item' + (currentIndex < 0 ? ' active' : '');
    openItem.innerHTML = `
        <div class="history-item-icon">📝</div>
        <div class="history-item-text">Open</div>
    `;
    openItem.onclick = () => {
        const hm = (window as any).historyManager;
        if (hm && typeof hm.jumpTo === 'function') hm.jumpTo(-1);
    };
    list.appendChild(openItem);

    actions.forEach((action, i) => {
        const item = document.createElement('div');
        const isGhosted = i > currentIndex;
        const isActive = i === currentIndex;
        
        item.className = 'history-item' + (isActive ? ' active' : '') + (isGhosted ? ' ghosted' : '');
        if (isGhosted) {
             item.style.opacity = '0.4'; // A bit lighter then 0.5 to show it's ghosted
        }

        let icon = '⚡';
        const desc = action.description || '';
        if (desc.includes('Pen') || desc.includes('Brush') || desc.includes('Draw')) icon = '✏️';
        else if (desc.includes('Layer')) icon = '📄';
        else if (desc.includes('Vector')) icon = '🔷';
        else if (desc.includes('Add')) icon = '➕';
        else if (desc.includes('Red Eye') || desc.includes('Eye')) icon = '👁️';

        item.innerHTML = `
            <div class="history-item-icon">${icon}</div>
            <div class="history-item-text">${desc}</div>
        `;

        item.onclick = () => {
            const hm = (window as any).historyManager;
            if (hm && typeof hm.jumpTo === 'function') hm.jumpTo(i);
        };

        list.appendChild(item);
    });
}

function emitHistoryChanged(hm: any) {
    // Collect actions from various potential HistoryManager formats
    const actions = hm.currentActions ?? hm._actions ?? hm.actions ?? [];
    const idx = hm.currentIdx ?? hm._currentIndex ?? hm.currentIndex ?? -1;
    
    const payload = {
        actions: actions.map((a: any, i: number) => ({
            description: a.description,
            active: i === idx,
            ghosted: i > idx
        })),
        currentIndex: idx
    };

    EventBus.emit('HISTORY_CHANGED', payload);
    EventBus.emit('RENDER_LAYERS');
    
    // Fallback UI update
    updateHistoryList(actions, idx);
    
    if ((window as any).updateUndoRedoUI) (window as any).updateUndoRedoUI();
}
